import * as React from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ChevronDown, RotateCcw } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/data/ConfirmDialog';
import { DateTime } from '@/components/data/DateTime';
import { EmptyState } from '@/components/data/EmptyState';
import { Input } from '@/components/ui/Input';
import { BaselineDiff } from '@/features/copy/BaselineDiff';
import { cn } from '@/lib/cn';
import { useApiError } from '@/lib/useApiError';
import { useCopyBundle, useCopyBundles, useRevertCopyBundle } from '@/queries/copy';
import {
  COPY_LOCALES,
  type CopyBundle,
  type CopyShape,
  type CopyValue,
} from '@/schemas/admin/copy';

export const Route = createFileRoute('/_authed/copy/bundles')({
  component: CopyBundlesScreen,
});

/** The snapshot carries values, not shapes — read the shape off the value. */
function inferShape(value: CopyValue | undefined | null): CopyShape {
  if (typeof value === 'string') return 'string';
  const first = Array.isArray(value) ? value[0] : undefined;
  return typeof first === 'object' && first !== null ? 'object_list' : 'string_list';
}

function CopyBundlesScreen() {
  const { t } = useTranslation('copy');
  const { t: tc } = useTranslation('common');
  const resolveError = useApiError();
  const list = useCopyBundles();
  const revert = useRevertCopyBundle();

  const [expanded, setExpanded] = React.useState<number | null>(null);
  const [revertTarget, setRevertTarget] = React.useState<number | null>(null);
  const [note, setNote] = React.useState('');

  const bundles = React.useMemo(
    () => (list.data ? list.data.pages.flatMap((p) => p.items) : []),
    [list.data]
  );

  async function onRevert(version: number) {
    try {
      const res = await revert.mutateAsync(
        note.trim() ? { version, note: note.trim() } : { version }
      );
      setRevertTarget(null);
      setNote('');
      toast.success(
        t('bundles.revertSuccess', { version: res.version, restored: res.reverted_to })
      );
    } catch (e) {
      setRevertTarget(null);
      toast.error(resolveError(e).message);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{t('bundles.title')}</h1>
          <p className="max-w-3xl text-sm text-[color:var(--color-fg-muted)]">
            {t('bundles.subtitle')}
          </p>
        </div>
        <Link
          to="/copy"
          className="rounded-[var(--radius-md)] border border-[color:var(--color-border)] px-3 py-2 text-sm font-semibold hover:bg-[color:var(--color-surface-2)]"
        >
          {t('bundles.backToCatalog')}
        </Link>
      </header>

      {list.isLoading ? (
        <p className="text-sm text-[color:var(--color-fg-muted)]">{tc('states.loading')}</p>
      ) : null}
      {list.error ? (
        <p role="alert" className="text-sm text-[color:var(--color-danger)]">
          {resolveError(list.error).message}
        </p>
      ) : null}

      {!list.isLoading && bundles.length === 0 ? (
        <EmptyState title={t('bundles.empty')} body={t('bundles.emptyBody')} />
      ) : null}

      <ol className="space-y-3">
        {bundles.map((bundle, index) => {
          const previous = bundles[index + 1]?.version ?? null;
          const isOpen = expanded === bundle.version;
          return (
            <li key={bundle.version}>
              <Card>
                <CardContent className="space-y-3 pt-6">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge tone="accent">{t('meta.bundleV', { version: bundle.version })}</Badge>
                        <Badge tone={bundle.source === 'revert' ? 'warn' : 'success'}>
                          {t(`bundles.source.${bundle.source}`)}
                        </Badge>
                        <span className="text-xs text-[color:var(--color-fg-muted)]">
                          <DateTime value={bundle.published_at} />
                        </span>
                      </div>
                      <p className="text-sm">
                        {bundle.note ? (
                          <span>{bundle.note}</span>
                        ) : (
                          <span className="italic text-[color:var(--color-fg-muted)]">
                            {t('bundles.noNote')}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-[color:var(--color-fg-muted)]">
                        {t('bundles.changedKeys', { count: bundle.changed_keys.length })}
                        {bundle.published_by ? (
                          <span dir="ltr" className="ms-2 font-mono">
                            {bundle.published_by.slice(0, 8)}…
                          </span>
                        ) : null}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-expanded={isOpen}
                        aria-controls={`bundle-${bundle.version}-diff`}
                        onClick={() => setExpanded(isOpen ? null : bundle.version)}
                      >
                        <ChevronDown
                          className={cn('size-4 transition-transform', isOpen && 'rotate-180')}
                          aria-hidden
                        />
                        {isOpen ? t('bundles.hideDiff') : t('bundles.showDiff')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setNote('');
                          setRevertTarget(bundle.version);
                        }}
                        disabled={revert.isPending}
                      >
                        <RotateCcw className="size-4" aria-hidden />
                        {t('bundles.revert')}
                      </Button>
                    </div>
                  </div>

                  <div id={`bundle-${bundle.version}-diff`} hidden={!isOpen}>
                    {isOpen ? <BundleDiff bundle={bundle} previousVersion={previous} /> : null}
                  </div>
                </CardContent>
              </Card>
            </li>
          );
        })}
      </ol>

      <div className="flex justify-center">
        {list.hasNextPage ? (
          <Button
            variant="outline"
            onClick={() => void list.fetchNextPage()}
            loading={list.isFetchingNextPage}
          >
            {t('table.loadMore')}
          </Button>
        ) : null}
      </div>

      <ConfirmDialog
        open={revertTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRevertTarget(null);
        }}
        title={t('bundles.revertConfirmTitle', { version: revertTarget ?? 0 })}
        description={
          <>
            <span className="block">{t('bundles.revertConfirmBody')}</span>
            <span className="mt-3 block">
              <label htmlFor="revert-note" className="mb-1 block text-xs font-semibold">
                {t('publish.noteLabel')}
              </label>
              <Input
                id="revert-note"
                value={note}
                maxLength={500}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t('publish.notePlaceholder')}
              />
            </span>
          </>
        }
        confirmLabel={t('bundles.revert')}
        cancelLabel={tc('actions.cancel')}
        destructive
        loading={revert.isPending}
        onConfirm={() => onRevert(revertTarget ?? 0)}
      />
    </div>
  );
}

/**
 * What this bundle actually changed: its snapshot against the previous
 * bundle's, key by key and locale by locale. Both snapshots are immutable, so
 * they are cached forever and the second visit costs nothing.
 */
function BundleDiff({
  bundle,
  previousVersion,
}: {
  bundle: CopyBundle;
  previousVersion: number | null;
}) {
  const { t } = useTranslation('copy');
  const { t: tc } = useTranslation('common');
  const resolveError = useApiError();
  const current = useCopyBundle(bundle.version);
  const previous = useCopyBundle(previousVersion);

  if (current.isPending || (previousVersion !== null && previous.isPending)) {
    return <p className="text-sm text-[color:var(--color-fg-muted)]">{tc('states.loading')}</p>;
  }
  if (current.error) {
    return (
      <p role="alert" className="text-sm text-[color:var(--color-danger)]">
        {resolveError(current.error).message}
      </p>
    );
  }
  if (!current.data) return null;

  const rows = bundle.changed_keys.flatMap((key) =>
    COPY_LOCALES.map((locale) => {
      const after = current.data.snapshot[locale]?.[key];
      const before = previous.data?.snapshot[locale]?.[key];
      return { key, locale, before, after };
    }).filter((r) => JSON.stringify(r.before ?? null) !== JSON.stringify(r.after ?? null))
  );

  if (rows.length === 0) {
    return <p className="text-sm text-[color:var(--color-fg-muted)]">{t('diff.noChanges')}</p>;
  }

  return (
    <ul className="mt-2 space-y-4">
      {rows.map((row) => (
        <li key={`${row.key}:${row.locale}`}>
          <p className="mb-1 flex flex-wrap items-center gap-2">
            <span dir="ltr" className="break-all font-mono text-xs font-semibold">
              {row.key}
            </span>
            <Badge tone="neutral">{row.locale.toUpperCase()}</Badge>
          </p>
          <BaselineDiff
            shape={inferShape(row.after ?? row.before)}
            locale={row.locale}
            before={row.before ?? null}
            beforeLabel={
              previousVersion === null
                ? t('bundles.noPrevious')
                : t('meta.bundleV', { version: previousVersion })
            }
            after={row.after ?? null}
            afterLabel={t('meta.bundleV', { version: bundle.version })}
          />
        </li>
      ))}
    </ul>
  );
}
